import { ViewIds } from '@ardoq/api-types';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { FocusedHoveredState } from '@ardoq/dependency-map';
import { HasViewInstanceId } from '@ardoq/graph';
import { connect, spreadMap } from '@ardoq/rxbeach';
import { useEffect, useRef, type ComponentType } from 'react';
import { combineLatest, map, shareReplay } from 'rxjs';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { SettingsBarAndViewContainer } from 'tabview/SettingsBarAndViewContainer';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import {
  createViewStatusReporter,
  registerWhileSubscribed,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { addToPresentation } from 'viewSettings/exportHandlers';
import {
  WORKSPACE_MODE_DEPENDENCY_MAP_VIEW_COMMANDS,
  type DependencyMapViewCommands,
} from '../commands';
import { DependencyMapView } from '../DependencyMapView';
import type {
  DependencyMapViewDataProps,
  DependencyMapViewModelState,
} from '../types';
import { EmptyState as EmptyStateBase } from '../EmptyState';
import {
  DependencyMapSettingsBar,
  DependencyMapSettingsBarProps,
} from '../view/DependencyMapSettingsBar';
import {
  getViewModel$,
  toWorkspaceModeViewModel,
  viewModelRoutines,
} from '../viewModel$';
import { toWorkspaceModeSettingsControlsDependencies } from './settingsControls';

type WorkspaceModeDependencyMapWithContainerProps = {
  view: DependencyMapViewDataProps;
  settings: Omit<
    DependencyMapSettingsBarProps,
    | 'containerRef'
    | 'onViewSettingsUpdate'
    | 'addToPresentation'
    | 'trackingFunction'
  >;
  isViewEmpty: boolean;
  noConnectedComponents: boolean;
  commands: DependencyMapViewCommands;
  viewStatusReporter: ViewStatusReporter;
};

const WorkspaceModeDependencyMapWithContainer = (
  props: WorkspaceModeDependencyMapWithContainerProps
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { commands, viewStatusReporter, isViewEmpty } = props;
  const { dataWorkToken } = props.view;

  useEffect(() => {
    if (isViewEmpty && dataWorkToken) {
      viewStatusReporter.reportWorkFinished(dataWorkToken);
    }
  }, [isViewEmpty, viewStatusReporter, dataWorkToken]);

  return (
    <SettingsBarAndViewContainer>
      <div className="menuContainer">
        <DependencyMapSettingsBar
          {...props.settings}
          onViewSettingsUpdate={onViewSettingsUpdate}
          addToPresentation={addToPresentation}
          trackingFunction={trackExportingVisualization}
          containerRef={containerRef}
        />
      </div>
      {props.isViewEmpty ? (
        <EmptyStateBase noConnectedComponents={props.noConnectedComponents} />
      ) : (
        <DependencyMapView
          {...props.view}
          containerRef={containerRef}
          commands={commands}
          contextMenuName={OBJECT_CONTEXT_MENU_NAME}
          viewStatusReporter={viewStatusReporter}
        />
      )}
    </SettingsBarAndViewContainer>
  );
};

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;

const WorkspaceModeDependencyMapView = ({
  viewInstanceId,
}: HasViewInstanceId) => {
  const connectedRef = useRef<ComponentType<{
    commands: typeof WORKSPACE_MODE_DEPENDENCY_MAP_VIEW_COMMANDS;
  }> | null>(null);
  if (!connectedRef.current) {
    const viewStatusReporter = createViewStatusReporter(viewInstanceId);
    const [coreViewModel$, interactiveStates$] = getViewModel$({
      viewInstanceId,
      viewSettingsWithChanges$: getWorkspaceModeViewSettingsWithChanges$(
        VIEW_ID,
        viewInstanceId
      ),
      viewStatusReporter,
      defaultViewSettings: getDefaultViewSettings(VIEW_ID, false),
    });

    const viewModel$ = combineLatest([
      coreViewModel$.pipe(
        map(toWorkspaceModeViewModel),
        withDataWork(viewStatusReporter, 'source')
      ),
      interactiveStates$,
      i18nLocale$,
    ]).pipe(
      spreadMap(toWorkspaceModeDependencyMapWithContainerProps),
      map(containerProps => ({ ...containerProps, viewStatusReporter })),
      registerWhileSubscribed(viewStatusReporter),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    connectedRef.current = connect(
      WorkspaceModeDependencyMapWithContainer,
      viewModel$,
      viewModelRoutines(viewModel$.pipe(map(({ view }) => view)))
    );
  }
  const ConnectedView = connectedRef.current;
  return (
    <ConnectedView commands={WORKSPACE_MODE_DEPENDENCY_MAP_VIEW_COMMANDS} />
  );
};

const toWorkspaceModeDependencyMapWithContainerProps = (
  viewModel: DependencyMapViewModelState & HasDataWorkToken,
  interactiveStates: FocusedHoveredState,
  i18nLocale: I18nLocale
): Omit<
  WorkspaceModeDependencyMapWithContainerProps,
  'commands' | 'viewStatusReporter'
> => ({
  view: {
    ...viewModel,
    ...interactiveStates,
    i18nLocale,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
  },
  settings: {
    viewSettings: viewModel.viewSettings,
    settingsControlsDependencies:
      toWorkspaceModeSettingsControlsDependencies(viewModel),
    isEmptyView: viewModel.isViewEmpty,
    quickPerspectiveReferenceTypeFilters:
      viewModel.quickPerspectiveReferenceTypeFilters,
    i18nLocale,
  },
  isViewEmpty: viewModel.isViewEmpty,
  noConnectedComponents: viewModel.viewModel.noConnectedComponents,
});

export { WorkspaceModeDependencyMapView };
